import { important } from 'csx';
import { TEAL_PRIMARY } from 'src/common-ui/theme';
import { style } from 'typestyle';

export const INPUT_SUGGEST_FONT_SIZE = '0.9rem';
export const generalPopperHeight = 250;
export const FONT_FAMILY = 'Roboto';
const container = style({
  $debugName: 'autocomplete-container',
  position: 'relative',
  width: '100%',
  fontFamily: important(`${FONT_FAMILY}, Arial, sans-serif`),
  $nest: {
    '.select-container': {
      position: 'relative',
      minWidth: '200px',
      width: '100%',
      maxWidth: '400px',
      backgroundColor: 'white',
    },
  },
});
export const generalPopupIndicator = style({
  $debugName: 'autocomplete-popper-general-indicator',
  transform: 'none !important',
  $nest: {
    '& > span:first-child': {
      borderLeft: '1px solid rgb(204, 204, 204) !important',
      paddingLeft: '6px',
    },
  },
});
export const generalPopper = style({
  $debugName: 'autocomplete-popper-general',
  boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 4px 11px !important',
  maxHeight: 'none',
});
export const actionButtons = style({
  $debugName: 'autocomplete-action-buttons',
  alignItems: 'center',
  backgroundColor: 'white',
  borderTop: '1px solid rgb(224, 224, 224)',
  display: 'flex',
  gap: '8px',
  justifyContent: 'flex-end',
  padding: '8px',
  $nest: {
    '& button': {
      borderRadius: '3px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.25)',
      height: '36px',
      width: '36px',
    },
    '& button:first-child': {
      backgroundColor: 'white',
      color: '#6b6b6b',
    },
    '& button:last-child': {
      backgroundColor: TEAL_PRIMARY,
      color: 'white',
    },
  },
});
export const customPopperStyle = style({
  $debugName: 'autocomplete-popper-fw',
  width: '100% !important',
});

const errorIcon = style({
  color: 'darkred',
  fontSize: '1.2rem',
  padding: '5px',
});

/**
 * Globally shared base autocomplete styles.
 *
 * The implementing (wrapping) component of InputSuggest is expected to provide necessary style override.
 *
 * Some of the keys are empty with a $debugName only.
 * These are mainly for debug purposes but show what `classes` keys are referenced by InputSuggest overrides.
 */
export const baseAutocompleteStyles = {
  root: style({
    $debugName: 'autocomplete-root',
  }),
  inputRoot: style({
    $debugName: 'autocomplete-input-root',
    fontSize: important(INPUT_SUGGEST_FONT_SIZE),
    $nest: {
      '&:disabled': {
        cursor: important('not-allowed'),
      },
    },
  }),
  input: style({
    $debugName: 'autocomplete-input',
    $nest: {
      '&:disabled': {
        cursor: important('not-allowed'),
      },
    },
  }),
  focused: style({
    $debugName: 'autocomplete-focused',
    borderColor: `${TEAL_PRIMARY} !important`,
  }),
  loading: style({
    fontSize: INPUT_SUGGEST_FONT_SIZE,
  }),
  noOptions: style({
    fontSize: INPUT_SUGGEST_FONT_SIZE,
  }),
  option: style({
    $debugName: 'autocomplete-option',
    fontSize: INPUT_SUGGEST_FONT_SIZE,
    // make sure the option fills the available vertical space
    height: 'stretch',
    $nest: {
      '&:hover': {
        backgroundColor: TEAL_PRIMARY,
        color: 'white',
        opacity: '45%',
      },
      '&[aria-selected="true"]': {
        backgroundColor: `${TEAL_PRIMARY} !important`,
        color: 'white',
      },
      '&[data-focus="true"]': {
        backgroundColor: `${TEAL_PRIMARY} !important`,
        color: 'white',
        opacity: '45%',
      },
    },
  }),
  endAdornment: style({
    $debugName: 'autocomplete-end-adorn',
    display: 'flex',
  }),
  tagSizeSmall: style({
    $debugName: 'autocomplete-tag-size',
    fontSize: 0.75,
  }),
  paper: style({
    $debugName: 'autocomplete-paper-root',
    // removes vertical spacing between the input and the popper rendering options
    margin: important('0'),
  }),
  listbox: style({
    $debugName: 'autocomplete-listbox',
    padding: important('0'),
  }),
};

export function getChipStyles(options?: { fullWidth: boolean }) {
  const fullWidth = options ? options.fullWidth : false;
  return {
    root: style({
      $debugName: 'autocomplete-chip-root',
      borderRadius: '2px !important',
      fontFamily: important('"Open Sans", Arial, sans-serif'),
      fontSize: INPUT_SUGGEST_FONT_SIZE,
      height: 22,
      width: fullWidth ? '100%' : undefined,
    }),
    deleteIcon: style({
      $debugName: 'autocomplete-chip-delete',
      height: '100% !important',
      margin: '0 !important',
      $nest: {
        '&:hover': {
          backgroundColor: 'rgb(255, 189, 173)',
          color: 'rgb(222, 53, 11) ',
        },
      },
    }),
    label: style({
      width: fullWidth ? '100%' : undefined,
    }),
  };
}

export default {
  container,
  errorIcon,
};
